/**
 * WalletDrillIn.jsx — one jar's settled entries, and one payday's stored chapter (#583/#584 N1 slice S14;
 * design D5, D8, T15, T23).
 *
 * Presentational: the host (`WalletRoute.jsx`) reads, maps (`lib/walletStoryMapping.js`) and owns focus and
 * the one polite status line. Each panel is a labelled `<section>` whose heading takes focus when it opens
 * (`headingRef`, `tabIndex={-1}`), and its Close button hands focus back to whatever opened it. The words say
 * "allocated", never "balance": there is no spending ledger yet, so a jar total is what settled paydays put in,
 * not what is left. A funded savings goal says "Funding target reached" and never that anything was bought.
 * Themes follow the Lantern tokens with literal fallbacks, and nothing has a fixed width, so 320 px wraps.
 */
import React, { useInsertionEffect } from 'react';

import { say } from '../lib/walletMapping.js';

const STYLE_ID = 'wdi-styles';
const STYLES = `
.wdi { margin: var(--s-3, 12px) 0; padding: var(--s-4, 16px); border-radius: var(--r-md, 14px);
  background: var(--surface-2, #1d1a24); color: var(--text-hi, #f4efe6); border: 1px solid var(--line, #3a3446); }
[data-theme="light"] .wdi { background: var(--surface-2, #f7f2ea); color: var(--text-hi, #221d18); border-color: var(--line, #d9cfc2); }
.wdi h3 { margin: 0 0 var(--s-2, 8px); font-size: var(--fs-lg, 1.125rem); }
.wdi h3:focus { outline: 2px solid var(--lantern-400, #f2b544); outline-offset: 2px; }
.wdi p { margin: 0 0 var(--s-2, 8px); overflow-wrap: anywhere; }
.wdi ul { margin: 0 0 var(--s-3, 12px); padding-left: 1.25em; }
.wdi li { margin: 0 0 4px; overflow-wrap: anywhere; }
.wdi__actions { display: flex; flex-wrap: wrap; gap: var(--s-2, 8px); }
.wdi__actions button { min-height: 44px; padding: 0 var(--s-3, 12px); border-radius: var(--r-sm, 10px);
  border: 1px solid currentColor; background: transparent; color: inherit; font: inherit; cursor: pointer; }
.wdi__actions button:focus-visible { outline: 2px solid var(--lantern-400, #f2b544); outline-offset: 2px; }
.wdi__candle { font-weight: 600; }
@media (forced-colors: active) { .wdi { border-color: CanvasText; } }
`;

function useStyles() {
  useInsertionEffect(() => {
    if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return undefined;
    const el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = STYLES;
    document.head.appendChild(el);
    return undefined;
  }, []);
}

function SavingsCandle({ savings }) {
  if (!savings) return null;
  const target = say(savings.target, savings.currency);
  return (
    <>
      <p className="wdi__candle" data-part="savings-candle">
        {savings.reached
          ? `${savings.status}: ${savings.label} (${target}).`
          : `${say(savings.saved, savings.currency)} of ${target} saved for ${savings.label}, from settled Save allocations only.`}
      </p>
      {savings.note ? <p>{savings.note}</p> : null}
      {savings.notCounted.length ? (
        <p>{`Save allocations settled in another currency are not counted toward this goal: ${savings.notCounted
          .map((n) => say(n.saved, n.currency)).join(', ')}.`}</p>
      ) : null}
    </>
  );
}

/**
 * @param {{ name: string, label: string, level: number|null, status: 'loading'|'ready'|'failed', view: object|null,
 *   failure: string, moreLoading: boolean, onMore: () => void, onClose: () => void, headingRef: object }} props
 */
export function JarEntriesPanel({ name, label, level, status, view, failure, moreLoading, onMore, onClose, headingRef }) {
  useStyles();
  const id = `wdi-jar-${name.toLowerCase()}`;
  let body;
  if (status === 'loading') body = <p>{`Loading your ${name} entries…`}</p>;
  else if (status === 'failed' || !view) body = <p>{failure}</p>;
  else {
    body = (
      <>
        {view.totals.length === 0
          ? <p>{`Nothing has been allocated to ${name} yet. Jars fill from settled paydays.`}</p>
          : view.totals.map((t) => (
            <p key={t.currency}>{`${say(t.allocated, t.currency)} allocated from ${t.count} settled payday ${t.count === 1 ? 'entry' : 'entries'}.`}</p>
          ))}
        {level !== null && level !== undefined ? <p>{`The jar’s fill shows your ${level}% split, not a target.`}</p> : null}
        {name === 'Save' ? <SavingsCandle savings={view.savings} /> : null}
        <p>{`Showing ${view.entries.length} of ${view.totals.reduce((n, t) => n + t.count, 0)} ${name} entries.`}</p>
        {view.entries.length ? (
          <ul aria-label={`${name} entries, newest first`}>
            {view.entries.map((e) => (
              <li key={e.id}>{`${e.when}: `}<span>{say(e.amount, e.currency)}</span>{e.adjustment ? ' (a correction)' : ''}</li>
            ))}
          </ul>
        ) : null}
      </>
    );
  }
  return (
    <section className="wdi" aria-labelledby={id} data-part="jar-entries">
      <h3 id={id} ref={headingRef} tabIndex={-1}>{label}</h3>
      {body}
      <div className="wdi__actions">
        {status === 'ready' && view && view.nextCursor ? (
          <button type="button" onClick={onMore} disabled={moreLoading}>{moreLoading ? 'Loading more…' : `Show more ${name} entries`}</button>
        ) : null}
        <button type="button" onClick={onClose}>{`Close ${name} entries`}</button>
      </div>
    </section>
  );
}

/**
 * @param {{ status: 'loading'|'ready'|'failed', view: object|null, failure: string, onClose: () => void,
 *   headingRef: object }} props
 */
export function ChapterPanel({ status, view, failure, onClose, headingRef }) {
  useStyles();
  const title = status === 'ready' && view
    ? `${view.adjustment ? 'Correction' : 'Payday'} chapter, settled ${view.settled}`
    : 'Payday chapter';
  let body;
  if (status === 'loading') body = <p>Loading this payday chapter…</p>;
  else if (status === 'failed' || !view) body = <p>{failure}</p>;
  else {
    const split = (jars) => jars.map((j) => `${j.name} ${view.say(j.amount)}`).join(' · ');
    // A correction is not a part the payday was paid in (#580 settles it with no batch key), so the two are
    // counted apart, and a correction's own chapter is never "(this one)" in the parts sentence.
    const parts = view.batches.filter((b) => !b.adjustment);
    const fixes = view.batches.filter((b) => b.adjustment && !b.current).map((b) => say(b.total, b.currency));
    const fixNote = !fixes.length ? null : fixes.length === 1
      ? `${view.adjustment ? 'Another' : 'A'} correction of ${fixes[0]} belongs to this period.`
      : `The ${view.adjustment ? 'other ' : ''}corrections of ${fixes.join(', ')} belong to this period.`;
    body = (
      <>
        <p>{`Total ${view.say(view.total)}: ${split(view.jars)}.`}</p>
        <ul aria-label="What this payday paid for">
          {view.items.map((i) => (
            <li key={i.id}>{`${i.correction ? 'A correction for work' : 'Work'} from ${i.when}: ${view.say(i.amount)} (${split(i.jars)})`}</li>
          ))}
        </ul>
        {parts.length > 1 ? (
          <p>{`This payday was paid in ${parts.length} parts: ${parts.map((b) => `${say(b.total, b.currency)}${b.current ? ' (this one)' : ''}`).join(', ')}.`}</p>
        ) : null}
        {fixes.length ? <p>{fixNote}</p> : null}
      </>
    );
  }
  return (
    <section className="wdi" aria-labelledby="wdi-chapter" data-part="chapter-detail">
      <h3 id="wdi-chapter" ref={headingRef} tabIndex={-1}>{title}</h3>
      {body}
      <div className="wdi__actions">
        <button type="button" onClick={onClose}>Close the payday chapter</button>
      </div>
    </section>
  );
}
